import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '/api';

const levelConfig = {
  gold: { label: 'Gold Excellence', color: '#92400e', bg: '#fef3c7', badge: '#f59e0b', border: '#d4a843' },
  silver: { label: 'Silver Merit', color: '#374151', bg: '#f1f5f9', badge: '#6b7280', border: '#94a3b8' },
  completion: { label: 'Completion', color: '#1e40af', bg: '#eff6ff', badge: '#3b82f6', border: '#3b82f6' },
};

export default function CDPCertificateVerify() {
  const { certNo: encodedCertNo } = useParams();
  const certNo = decodeURIComponent(encodedCertNo);
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/cdp/certificate/verify/${certNo}`);
        const data = await res.json();
        if (!res.ok || !data.valid) { setError(data.error || 'Certificate not found'); return; }
        setCert(data);
      } catch { setError('Failed to verify certificate'); }
      finally { setLoading(false); }
    })();
  }, [certNo]);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <p style={{ color: '#64748b', fontSize: '16px' }}>Verifying certificate...</p>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div style={{ textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', maxWidth: '420px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10060;</div>
        <h2 style={{ color: '#991b1b', margin: '0 0 8px' }}>Invalid Certificate</h2>
        <p style={{ color: '#6b7280', margin: 0 }}>{error}</p>
      </div>
    </div>
  );

  const lc = levelConfig[cert.level] || levelConfig.completion;
  const dateStr = new Date(cert.issuedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '32px 16px' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto' }}>
        {/* Verified badge */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#d1fae5', color: '#065f46', padding: '8px 20px', borderRadius: '24px', fontSize: '14px', fontWeight: '600' }}>
            &#9989; Verified Certificate
          </span>
        </div>

        {/* Certificate card */}
        <div style={{ background: '#fff', borderRadius: '20px', boxShadow: '0 8px 40px rgba(0,0,0,0.1)', overflow: 'hidden', border: `2px solid ${lc.border}` }}>
          {/* Header band */}
          <div style={{ background: lc.bg, padding: '32px 40px 24px', textAlign: 'center', borderBottom: `2px solid ${lc.border}` }}>
            <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '600', letterSpacing: '2px', color: '#6b7280', textTransform: 'uppercase' }}>Commercial Drafting Programme</p>
            <p style={{ margin: '0 0 8px', fontSize: '11px', color: '#9ca3af' }}>AskNitin</p>
            <div style={{ width: '60px', height: '2px', background: lc.border, margin: '12px auto 16px' }} />
            <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#6b7280', letterSpacing: '1px', textTransform: 'uppercase' }}>Certificate of</p>
            <h1 style={{ margin: '0', fontSize: '32px', fontWeight: '800', color: lc.badge }}>{lc.label}</h1>
          </div>

          {/* Body */}
          <div style={{ padding: '32px 40px 28px', textAlign: 'center' }}>
            <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#9ca3af' }}>Awarded to</p>
            <h2 style={{ margin: '0 0 20px', fontSize: '28px', fontWeight: '700', color: lc.color }}>{cert.userName}</h2>

            <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#4b5563', lineHeight: '1.6' }}>
              For Successfully Completing Practice Sessions on Drafting Commercial Contracts
            </p>

            {cert.documentTitles?.length > 0 && (
              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', textAlign: 'left' }}>
                <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px' }}>Documents Completed</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {cert.documentTitles.map((t, i) => (
                    <span key={i} style={{ background: lc.bg, color: lc.color, padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '500', border: `1px solid ${lc.border}33` }}>{t}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Meta */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderTop: '1px solid #e5e7eb', fontSize: '12px', color: '#9ca3af' }}>
              <span>Certificate No: <strong style={{ color: '#6b7280', fontFamily: 'monospace' }}>{cert.certificateNumber}</strong></span>
              <span>Issued: <strong style={{ color: '#6b7280' }}>{dateStr}</strong></span>
            </div>
          </div>
        </div>

        {/* Download button */}
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <a
            href={cert.pdfUrl || `${API}/cdp/certificate/verify/${encodeURIComponent(cert.certificateNumber)}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: lc.color, color: '#fff', padding: '12px 32px', borderRadius: '12px', fontSize: '15px', fontWeight: '600', textDecoration: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
          >
            &#128196; Download PDF Certificate
          </a>
        </div>

        <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '11px', color: '#b0b8c4' }}>
          This certificate has been verified by AskNitin's Commercial Drafting Programme.
        </p>
      </div>
    </div>
  );
}
